export type RoutePoint = { lat?: string | number; lng?: string | number; latitude?: string | number; longitude?: string | number };
export type RoutePaths = Record<string, RoutePoint[][]>;

type StopPosition = { lat: string | number; lng: string | number; nextStopsByRoute?: Record<string, string[]> };

function headingName(degrees: number) {
  return ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'][Math.round(degrees / 45) % 8];
}

/** Fallback only: the closest directed route segment may be ambiguous on loops. */
export function routeHeadingNearStop(stop: StopPosition, paths: RoutePoint[][] | undefined): string | null {
  const lat = Number(stop.lat);
  const lng = Number(stop.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !paths?.length) return null;
  const scaleX = Math.cos(lat * Math.PI / 180) * 111_320;
  const scaleY = 111_320;
  let closest = Infinity;
  let heading: number | null = null;
  for (const path of paths) for (let index = 1; index < path.length; index += 1) {
    const previous = path[index - 1];
    const current = path[index];
    const ax = (Number(previous.lng ?? previous.longitude) - lng) * scaleX;
    const ay = (Number(previous.lat ?? previous.latitude) - lat) * scaleY;
    const bx = (Number(current.lng ?? current.longitude) - lng) * scaleX;
    const by = (Number(current.lat ?? current.latitude) - lat) * scaleY;
    if (![ax, ay, bx, by].every(Number.isFinite)) continue;
    const dx = bx - ax;
    const dy = by - ay;
    const length = dx * dx + dy * dy;
    if (length < 1) continue;
    const t = Math.max(0, Math.min(1, -(ax * dx + ay * dy) / length));
    const distance = Math.hypot(ax + t * dx, ay + t * dy);
    if (distance < closest) {
      closest = distance;
      heading = (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360;
    }
  }
  return closest <= 125 && heading != null ? headingName(heading) : null;
}

export function directionForStopRoute(stop: StopPosition, routeId: string, paths: RoutePaths) {
  const nextStops = stop.nextStopsByRoute?.[routeId]?.filter(Boolean) ?? [];
  if (nextStops.length === 1) return { label: `Toward ${nextStops[0]}`, source: 'Provider stop order' };
  if (nextStops.length > 1) return { label: `Toward ${nextStops.slice(0, 2).join(' / ')}${nextStops.length > 2 ? ` +${nextStops.length - 2}` : ''}`, source: 'Provider stop order; route variants' };
  const heading = routeHeadingNearStop(stop, paths[routeId]);
  return heading ? { label: `Heads ${heading} near this stop`, source: 'Estimated from mapped route line' } : { label: 'Direction not provided', source: 'Check the stop sign or tracker' };
}
